// Glossary words in the ESV's text. The commentary's are marked by the markdown
// renderer; the ESV's HTML is never stored, so the only place its words can be
// marked is here, once `Passage` has built the verses.
import { termAttrs, type TermMatcher } from "@bibleunleashed/shared";
import { SKIP } from "@/reader/highlights";

/**
 * Wrap each glossary word in the verses in a `<span class="term">`. Only text
 * inside a `.v` is read — never a verse number, a footnote marker or a heading —
 * and a word split across two text nodes, either side of a footnote, is left
 * unmarked. The wrapper adds no characters, so a highlight's offsets into the
 * verse's plain text mean the same with it as without.
 */
export function markTerms(root: Element, terms: TermMatcher | null): void {
  if (!terms) return;
  // Collected first: wrapping as we walk would pull text out from under the walker.
  const nodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const parent = n.parentElement;
    if (parent?.closest(".v") && !parent.closest(`${SKIP}, .term`)) nodes.push(n as Text);
  }
  for (const node of nodes) {
    // Back to front, so each split leaves the earlier matches' offsets as they were.
    for (const m of terms.find(node.data).reverse()) {
      const word = node.splitText(m.start);
      word.splitText(m.end - m.start);
      const span = document.createElement("span");
      for (const [name, value] of termAttrs(m.id)) span.setAttribute(name, value);
      word.replaceWith(span);
      span.append(word);
    }
  }
}
